<template>
	<Transition
		:enter-active-class="$style['dropdown-menu--opening']"
		:enter-class="$style['dropdown-menu--open-start']"
		:enter-to-class="$style['dropdown-menu--open-end']"
		:leave-active-class="$style['dropdown-menu--closing']"
		:leave-to-class="$style['dropdown-menu--closed-end']"
		@after-enter="afterEnter"
		@after-leave="afterLeave"
	>
		<div
			v-if="isOpen"
			v-testid="testId"
			v-bind="$attrs"
			:class="[
				$style['dropdown-menu'],
				effectivePosition === 'top' && $style['dropdown-menu--top'],
				effectivePosition === 'bottom' && $style['dropdown-menu--bottom'],
				effectivePosition === 'left' && $style['dropdown-menu--left'],
				effectivePosition === 'right' && $style['dropdown-menu--right'],
				effectiveStyle && $style['dropdown-menu--fixed']
			]"
			:style="effectiveStyle"
			@click.self="() => handleOffsetClick && close()"
		>
			<slot />
		</div>
	</Transition>
</template>
<script>
export default {
	name: 'DropdownMenu',
	inheritAttrs: false,
	props: {
		handleOffsetClick: {
			type: Boolean,
			default: true
		},
		position: {
			type: String,
			default: 'bottom'
		},
		fixedStyle: {
			type: Object,
			default: null
		},
		autoPosition: {
			type: Boolean,
			default: false
		},
		align: {
			type: String,
			default: 'left'
		},
		minWidth: {
			type: Number,
			default: 130
		},
		matchWidth: {
			type: Boolean,
			default: true
		},
		gap: {
			type: Number,
			default: 4
		},
		testId: {
			type: String,
			default: null
		}
	},
	data () {
		return {
			isOpen: false,
			computedPosition: null,
			computedFixedStyle: null,
			repositionHandler: null,
			repositionRafId: null
		}
	},
	computed: {
		effectivePosition () {
			return this.computedPosition || this.position
		},
		effectiveStyle () {
			return this.fixedStyle || this.computedFixedStyle || null
		}
	},

	beforeDestroy () {
		this.removeRepositionListeners()
		this.$root.$el.removeEventListener('click', this.globalClick)
		document.removeEventListener('keydown', this.globalEsc)
	},
	methods: {
		afterEnter () {
			this.$root.$el.addEventListener('click', this.globalClick)
			this.$emit('animation:entered')
			document.addEventListener('keydown', this.globalEsc)
		},
		afterLeave () {
			this.$root.$el.removeEventListener('click', this.globalClick)
			this.$emit('animation:left')
			document.removeEventListener('keydown', this.globalEsc)
			this.computedFixedStyle = null
			this.computedPosition = null
		},
		toggle () {
			if (this.isOpen) {
				this.close()
			} else {
				this.open()
			}
		},
		open () {
			if (this.autoPosition) {
				this.updatePosition()
				this.bindRepositionListeners()
			}
			this.isOpen = true
			if (this.autoPosition) {
				this.$nextTick(() => {
					this.updatePosition()
				})
			}
		},
		close () {
			this.isOpen = false
			this.removeRepositionListeners()
			this.$emit('close')
		},
		resolveTriggerEl () {
			const el = this.$el
			if (el && el.parentElement) {
				return el.parentElement
			}
			if (el && el.parentNode instanceof Element) {
				return el.parentNode
			}
			return null
		},
		estimateMenuHeight () {
			return 130
		},
		measureMenuHeight (menuEl) {
			if (!menuEl || typeof menuEl.getBoundingClientRect !== 'function') {
				return 0
			}

			const layout = menuEl.offsetHeight
			if (layout) return layout

			const visual = menuEl.getBoundingClientRect().height
			if (visual) return visual

			const originalTransform = menuEl.style.transform
			const originalOpacity = menuEl.style.opacity
			menuEl.style.transform = 'none'
			menuEl.style.opacity = '0'
			const height = menuEl.getBoundingClientRect().height
			menuEl.style.transform = originalTransform
			menuEl.style.opacity = originalOpacity
			return height
		},
		updatePosition () {
			const triggerEl = this.resolveTriggerEl()
			if (!triggerEl) return

			const triggerRect = triggerEl.getBoundingClientRect()
			const viewportHeight = window.innerHeight
			const spaceBelow = viewportHeight - triggerRect.bottom
			const spaceAbove = triggerRect.top

			const menuEl = this.$el instanceof Element ? this.$el : null
			let menuHeight = this.estimateMenuHeight()
			if (menuEl) {
				const measured = this.measureMenuHeight(menuEl)
				if (measured) {
					menuHeight = measured
				}
			}

			const horizontal = this.align === 'right'
				? { right: (window.innerWidth - triggerRect.right) + 'px', left: 'auto' }
				: { left: triggerRect.left + 'px', right: 'auto' }
			const explicitPosition = this.$options.propsData && this.$options.propsData.position
			const forced = ['top', 'bottom'].includes(explicitPosition) ? explicitPosition : null

			const size = {}
			if (this.matchWidth) {
				size.width = this.minWidth
					? Math.max(triggerRect.width, this.minWidth) + 'px'
					: triggerRect.width + 'px'
			} else if (this.minWidth) {
				size.minWidth = this.minWidth + 'px'
			}
			let style
			if ((forced === 'bottom') || (!forced && spaceBelow >= menuHeight + this.gap)) {
				this.computedPosition = 'bottom'
				style = {
					position: 'fixed',
					top: triggerRect.bottom + this.gap + 'px',
					bottom: 'auto',
					...size,
					zIndex: 10000
				}
			} else if ((forced === 'top') || spaceAbove >= menuHeight + this.gap) {
				this.computedPosition = 'top'
				style = {
					position: 'fixed',
					top: 'auto',
					bottom: (viewportHeight - triggerRect.top + this.gap) + 'px',
					...size,
					zIndex: 10000
				}
			} else if (spaceAbove >= spaceBelow) {
				this.computedPosition = 'top'
				style = {
					position: 'fixed',
					top: 'auto',
					bottom: (viewportHeight - triggerRect.top + this.gap) + 'px',
					...size,
					maxHeight: Math.max(spaceAbove - this.gap, 0) + 'px',
					overflowY: 'auto',
					zIndex: 10000
				}
			} else {
				this.computedPosition = 'bottom'
				style = {
					position: 'fixed',
					top: triggerRect.bottom + this.gap + 'px',
					bottom: 'auto',
					...size,
					maxHeight: Math.max(spaceBelow - this.gap, 0) + 'px',
					overflowY: 'auto',
					zIndex: 10000
				}
			}

			this.computedFixedStyle = { ...horizontal, ...style }
		},
		bindRepositionListeners () {
			if (!this.repositionHandler) {
				this.repositionHandler = () => {
					if (this.repositionRafId) return
					this.repositionRafId = requestAnimationFrame(() => {
						this.repositionRafId = null
						this.updatePosition()
					})
				}
				window.addEventListener('scroll', this.repositionHandler, true)
				window.addEventListener('resize', this.repositionHandler)
			}
		},
		removeRepositionListeners () {
			if (this.repositionHandler) {
				window.removeEventListener('scroll', this.repositionHandler, true)
				window.removeEventListener('resize', this.repositionHandler)
				this.repositionHandler = null
			}
			if (this.repositionRafId) {
				cancelAnimationFrame(this.repositionRafId)
				this.repositionRafId = null
			}
		},
		globalClick (e) {
			if (e.target !== this.$el && !this.$el.contains(e.target)) {
				if (this.handleOffsetClick) {
					this.close()
				} else {
					this.$emit('offset:click', e)
				}
			}
		},
		globalEsc (e) {
			if (e.keyCode === 27 || e.key === 'Escape') {
				this.close()
			}
		}
	}
}
</script>
<style lang="scss" module>
.dropdown-menu {
    z-index: 9999;
    position: absolute;
    background-color: getColor(surface-elevated);
    border-radius: 8px;
    box-shadow: 0 4px 12px 0 getColor(shadow);
    will-change: transform, opacity;
    transform-origin: top;

    @include dark-mode {
        border: 1px solid transparent;
        box-shadow: 0 0 0 1px getColor(border-darker), 0 8px 24px getColor(shadow), 0 2px 8px getColor(shadow-light);
    }

    &--top {
        transform-origin: bottom;
    }

    &--bottom {
        transform-origin: top;
    }

    &--left {
        transform-origin: left;
    }

    &--right {
        transform-origin: right;
    }

    &--fixed {
        position: fixed;
    }

    &--opening {
        transition: opacity 0.03s linear, transform 0.12s cubic-bezier(0, 0, 0.2, 1);
    }

    &--open-start {
        transform: scale(0);
        opacity: 0;
    }

    &--open-end {
        transform: scale(1);
        opacity: 1;
    }

    &--closing {
        transition: opacity 0.075s linear;
    }

    &--closed-end {
        opacity: 0;
    }
}
</style>
